import { CSSProperties, KeyboardEvent, useEffect, useRef, useState } from "react";
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { Button } from "@twilio-paste/core/button";
import { Label } from "@twilio-paste/core/label";
import { TextArea } from "@twilio-paste/core/textarea";

import { CSAT_SCALE_OPTIONS } from "./csat/csatScales";
import { useCsat } from "./csat/useCsat";
import {
    cabecalhoAtendimentoStyles,
    commentWrapperStyles,
    containerAtendimentoStyles,
    containerStyles,
    escalaAtendimentoStyles,
    optionStyles,
    scaleGroupStyles,
    titleStyles,
    tituloAtendimentoStyles
} from "./styles/CsatCard.styles";

const COMMENT_MAX = 500;

/*
 * A cor da opção NÃO escolhida — estrela apagada, emoji não selecionado.
 *
 * Era `#c9cdd1` com `opacity: 0.5`, o que compõe `rgb(222,224,227)` e dá **1,2:1** contra o fundo da
 * faixa e contra o branco da despedida: bem abaixo do mínimo de 3:1 que a WCAG pede para elemento
 * gráfico. A escala é a única affordance do card — se as cinco estrelas não se distinguem do fundo,
 * ninguém sabe que existem cinco. Este cinza, sem opacidade, mede **3,46:1** sobre a faixa e
 * **3,80:1** sobre o branco.
 */
const OPCAO_APAGADA = "#79839a";

/*
 * O texto da faixa, quando o brand-config não manda o dele.
 *
 * Na despedida o atendimento acabou e o passado cabe. Na faixa ele está acontecendo — o atendente
 * está do outro lado, digitando. Daí o imperativo no presente: pedir e não perguntar, e nada que
 * sugira encerramento, porque o apostador continua conversando depois de avaliar.
 */
const TITULO_NO_ATENDIMENTO = "Avalie o atendimento";
/* Curto de propósito: na faixa ele divide a largura com a escala e o dispensar. */
const COMENTARIO_NO_ATENDIMENTO = "Deixe um comentário";

/*
 * Quanto tempo o agradecimento fica na faixa antes de ela sair sozinha da tela.
 *
 * Existe porque a faixa deixou de depender de `csatPending` para continuar visível depois do envio
 * (ver o selector). Sem um fim próprio, "se mantém" viraria "fica para sempre": um "Obrigado"
 * grudado acima do composer pelo resto da conversa — que é exatamente o defeito da proposta que
 * recusamos quando o synccx ofereceu simplesmente não desligar o sinal.
 */
const MS_DO_AGRADECIMENTO_NA_FAIXA = 5000;

/*
 * CSAT card (v2 pull model) — apresentação. Toda a rede/estado do CSAT vive no hook useCsat; aqui fica
 * só o gate de exibição (plano §4), o render da escala do SNAPSHOT e a interação (seleção + teclado +
 * comentário + envio). Sem config/pesquisa ⇒ não renderiza nada (despedida idêntica a hoje).
 *
 * Duas variantes desde 2026-09-12. `atendimento` é a faixa compacta presa acima do composer, para a
 * pesquisa disparada por macro NO MEIO da conversa: ali o card divide a tela com um atendimento em
 * andamento e é assunto secundário. `despedida` é o caminho que já existia — e é o DEFAULT de
 * propósito, para nenhum chamador antigo mudar de comportamento por causa desta prop.
 */
type VarianteDoCard = "despedida" | "atendimento";

export const CsatCard = ({ variante = "despedida" }: { variante?: VarianteDoCard }) => {
    const { config, survey, submit, aposentarFaixa } = useCsat();
    const noAtendimento = variante === "atendimento";

    const [selectedScore, setSelectedScore] = useState<number | undefined>(survey?.score);
    const [hoverScore, setHoverScore] = useState<number | undefined>(undefined); // preview de hover (estrelas)
    const [comment, setComment] = useState<string>(survey?.comment ?? "");
    const [busy, setBusy] = useState(false);
    /*
     * Dispensar e expandir são LOCAIS, e só existem na variante do atendimento.
     *
     * O descarte não é persistido: recarregar traz o card de volta, porque o sinal continua carimbado
     * na conversa e a pesquisa continua pendente de verdade. Persistir exigiria um campo novo no
     * contrato com o synccx — não foi pedido, e inventá-lo aqui criaria um estado que só este widget
     * conhece.
     */
    const [dispensado, setDispensado] = useState(false);
    const [expandido, setExpandido] = useState(false);
    const optionRefs = useRef<(HTMLElement | null)[]>([]);

    // Edição: quando a pesquisa chega já respondida (allowEdit), pré-preenche nota/comentário.
    useEffect(() => {
        if (survey?.answered) {
            if (typeof survey.score === "number") {
                setSelectedScore(survey.score);
            }
            if (typeof survey.comment === "string") {
                setComment(survey.comment);
            }
        }
    }, [survey?.answered, survey?.score, survey?.comment]);

    /*
     * A faixa se aposenta sozinha depois do agradecimento — só a faixa, e só no desfecho terminal.
     *
     * Na despedida o card é o fim do caminho: não há tela para devolver, e sumir ali seria tirar da
     * pessoa a única confirmação de que a nota foi registrada.
     */
    const agradecendoNaFaixa = noAtendimento && (survey?.status === "submitted" || survey?.status === "readonly");
    useEffect(() => {
        if (!agradecendoNaFaixa) {
            return undefined;
        }
        const timer = setTimeout(aposentarFaixa, MS_DO_AGRADECIMENTO_NA_FAIXA);
        return () => clearTimeout(timer);
    }, [agradecendoNaFaixa, aposentarFaixa]);

    if (!config?.enabled || !survey?.requested || dispensado) {
        return null;
    }

    const { appearance } = config;
    /* Texto por variante, com o brand-config vencendo o default do widget nas duas pontas. */
    const titulo = noAtendimento ? appearance.titleDuringChat || TITULO_NO_ATENDIMENTO : appearance.title;
    const rotuloComentario = noAtendimento
        ? appearance.comment.labelDuringChat || COMENTARIO_NO_ATENDIMENTO
        : appearance.comment.label;

    /*
     * Estados terminais (mostram mensagem, não a escala) — antes do gate de janela/edição.
     *
     * O agradecimento respeita a variante. No atendimento ele continua sendo uma FAIXA: devolver o
     * bloco centralizado da despedida faria um retângulo alto saltar entre a conversa e o composer,
     * empurrando a conversa para cima no momento em que a pessoa acabou de dizer que estava tudo bem.
     */
    const terminal = (texto: string) =>
        noAtendimento ? (
            <Box {...containerAtendimentoStyles} data-test="csat-card-atendimento">
                <Text as="p" {...tituloAtendimentoStyles}>
                    {texto}
                </Text>
            </Box>
        ) : (
            <Box {...containerStyles} data-test="csat-card">
                <Text as="p" {...titleStyles}>
                    {texto}
                </Text>
            </Box>
        );

    if (survey.status === "submitted") {
        return terminal(appearance.thankYouText);
    }
    if (survey.status === "readonly") {
        return terminal("Obrigado pela sua resposta!");
    }

    // Gate §4: janela expirada ou já respondida sem edição ⇒ não renderiza (despedida limpa).
    const expired = Date.now() >= Date.parse(survey.expiresAt);
    const answeredLocked = survey.answered && !survey.allowEdit;
    if (expired || answeredLocked) {
        return null;
    }

    const options = CSAT_SCALE_OPTIONS[survey.scale] ?? CSAT_SCALE_OPTIONS.stars;
    const selectedIndex = options.findIndex((option) => option.score === selectedScore);
    const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0; // roving tabindex

    // Cor da marca (segue o chat) — a config do CSAT (primaryColor) vence se a marca a definiu.
    const brandColor = appearance.primaryColor || "var(--primary-button)";
    // Estrelas: preenchimento CUMULATIVO (1..N acesas). Carinhas/joinha: emoji solto, seleção única.
    // Números: caixinha com destaque de seleção. hoverScore dá o preview em estrelas e emoji.
    const isStar = survey.scale === "stars";
    const isEmoji = survey.scale === "faces" || survey.scale === "thumbs";
    const previewScore = hoverScore ?? selectedScore ?? 0;
    const previewSingle = hoverScore ?? selectedScore; // escolhido/hover (emoji = seleção única)
    const TRANSITION = "color 120ms ease, opacity 120ms ease, transform 80ms ease, background 120ms ease";

    const optionInlineStyle = (option: { score: number }, checked: boolean): CSSProperties => {
        if (isStar) {
            /*
             * Estrela acesa = cor da marca; apagada = cinza legível; sem moldura (só o glifo).
             * Menor no atendimento: ali a faixa divide a janela com uma conversa viva, e o tamanho da
             * despedida — onde o card é dono da tela — empurraria a conversa para cima.
             */
            const filled = option.score <= previewScore;
            return {
                border: "none",
                background: "transparent",
                padding: "4px",
                fontSize: noAtendimento ? "26px" : "30px",
                lineHeight: "1",
                color: filled ? brandColor : OPCAO_APAGADA,
                transform: filled ? "scale(1.08)" : "scale(1)",
                transition: TRANSITION
            };
        }
        if (isEmoji) {
            // emoji SOLTO (sem moldura fixa), maior; o escolhido/hover ganha fundo suave + leve zoom.
            const active = option.score === previewSingle;
            return {
                border: "none",
                background: active ? "#e9ebee" : "transparent",
                borderRadius: "14px",
                padding: "6px",
                fontSize: noAtendimento ? "28px" : "34px",
                lineHeight: "1",
                opacity: active ? 1 : 0.6,
                transform: active ? "scale(1.14)" : "scale(1)",
                transition: TRANSITION
            };
        }
        // numbers: caixinha (optionStyles) + destaque de seleção única na cor da marca.
        return checked ? { borderColor: brandColor, color: brandColor } : {};
    };

    const moveSelection = (fromIndex: number, delta: number) => {
        const next = (fromIndex + delta + options.length) % options.length;
        setSelectedScore(options[next].score);
        optionRefs.current[next]?.focus();
    };

    const handleOptionKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            moveSelection(index, 1);
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            event.preventDefault();
            moveSelection(index, -1);
        }
    };

    /*
     * O envio em voo vive no STORE, não aqui. `busy` e o `submittingRef` do hook morrem no desmonte,
     * e a troca de variante desmonta: enviar na faixa e a conversa encerrar antes da resposta fazia a
     * despedida nascer com tudo zerado e aceitar um SEGUNDO POST para a mesma pesquisa. O
     * `status: "submitting"` é o único sinal que atravessa.
     */
    const enviando = busy || survey.status === "submitting";

    const enviar = async (score: number, texto: string) => {
        if (enviando) {
            return;
        }
        setBusy(true);
        await submit(score, texto);
        setBusy(false);
    };

    const handleSubmit = async () => {
        if (selectedScore === undefined) {
            return;
        }
        await enviar(selectedScore, comment);
    };

    /*
     * O clique na nota faz coisas diferentes nas duas variantes, e a diferença é a razão de o card do
     * atendimento existir: ali responder tem de ser UM clique, senão a pesquisa vira mais uma tarefa
     * no meio de uma conversa e se aprende a ignorá-la.
     *
     * Com comentário habilitado o clique EXPANDE em vez de enviar — enviar direto faria o campo de
     * comentário nascer inalcançável, porque o envio já teria acontecido.
     */
    const comentarioHabilitado = appearance.comment.enabled;
    const handleEscolha = (score: number) => {
        setSelectedScore(score);
        if (!noAtendimento) {
            return;
        }
        if (comentarioHabilitado) {
            setExpandido(true);
        } else {
            void enviar(score, "");
        }
    };

    /*
     * A escala é a MESMA nas duas variantes — só o contêiner muda de forma. Duplicá-la aqui seria o
     * gêmeo clássico: duas cópias corretas hoje, divergentes na primeira mudança de escala.
     */
    const escala = (
        <Box
            {...(noAtendimento ? escalaAtendimentoStyles : scaleGroupStyles)}
            role="radiogroup"
            aria-label={titulo}
        >
                {options.map((option, index) => {
                    const checked = selectedScore === option.score;
                    return (
                        <Box
                            key={option.score}
                            ref={(el: HTMLElement | null) => {
                                optionRefs.current[index] = el;
                            }}
                            as="button"
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            aria-label={option.ariaLabel}
                            tabIndex={index === tabbableIndex ? 0 : -1}
                            onClick={() => handleEscolha(option.score)}
                            onKeyDown={(event: KeyboardEvent<HTMLElement>) => handleOptionKeyDown(event, index)}
                            onMouseEnter={isStar || isEmoji ? () => setHoverScore(option.score) : undefined}
                            onMouseLeave={isStar || isEmoji ? () => setHoverScore(undefined) : undefined}
                            {...optionStyles}
                            style={optionInlineStyle(option, checked)}
                        >
                            {option.label}
                        </Box>
                    );
                })}
        </Box>
    );

    const comentarioEBotao = (
        <>
            {comentarioHabilitado && (
                <Box {...commentWrapperStyles}>
                    <Label htmlFor="csat-comment">{rotuloComentario}</Label>
                    <TextArea
                        id="csat-comment"
                        maxLength={COMMENT_MAX}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    />
                </Box>
            )}

            <Button
                variant="primary"
                data-test="csat-submit"
                disabled={selectedScore === undefined || enviando}
                onClick={handleSubmit}
            >
                Enviar
            </Button>
        </>
    );

    if (noAtendimento) {
        return (
            <Box {...containerAtendimentoStyles} data-test="csat-card-atendimento">
                <Box {...cabecalhoAtendimentoStyles}>
                    <Text as="p" {...tituloAtendimentoStyles}>
                        {titulo}
                    </Text>
                    {/*
                     * Dispensar é desfecho explícito, e existe SÓ aqui: na despedida o card já é o fim
                     * do caminho, e um X ali só ofereceria uma saída para algo que ninguém precisa sair.
                     */}
                    <Box
                        as="button"
                        type="button"
                        title="Dispensar pesquisa"
                        aria-label="Dispensar pesquisa"
                        onClick={() => setDispensado(true)}
                        cursor="pointer"
                        border="none"
                        backgroundColor="transparent"
                        color="colorTextWeak"
                        padding="space20"
                        fontSize="fontSize40"
                        lineHeight="lineHeight40"
                    >
                        ×
                    </Box>
                </Box>

                {escala}

                {/* Só depois de escolher a nota, e só quando há comentário a dar: colapsado são duas linhas. */}
                {expandido && comentarioEBotao}
            </Box>
        );
    }

    return (
        <Box {...containerStyles} data-test="csat-card">
            <Text as="p" {...titleStyles}>
                {titulo}
            </Text>

            {escala}

            {comentarioEBotao}
        </Box>
    );
};
